import { useTranslation } from "react-i18next"
import { CircleHelp } from "lucide-react"

import {
  Input,
  Label,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"

export interface BaseFieldProps<T> {
  field: T & {
    name: string
    /*Human-readable question text shown to end users.*/
    question: string
    /**
     * Human-defined key used for analytics / column names.
     * When building submissions we use `label` as the key.
     */
    label: string
    required: boolean
    placeholder: string
  }
  onChange: (next: T) => void
  disabled?: boolean
  showPlaceholder?: boolean
}

export const BaseFieldEditor = <T extends { name: string }>({
  field,
  onChange,
  disabled = false,
  showPlaceholder = true,
}: BaseFieldProps<T>) => {
  const { t } = useTranslation()

  const slugify = (value: string): string => {
    const base = value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
    return base.length ? base : "field"
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="space-y-1">
        <Label className="text-xs">
          {t("workspace.agent.basicForm.formBuilder.baseField.question")}
        </Label>
        <Input
          value={field.question}
          onChange={(e) => {
            const nextQuestion = e.target.value
            const generatedName = slugify(nextQuestion)
            onChange({
              ...field,
              question: nextQuestion,
              // Preserve any existing analytics label; only generate name if missing.
              name: (field.name || generatedName) as T["name"],
            } as T)
          }}
          disabled={disabled}
        />
      </div>
      {showPlaceholder ? (
        <div className="space-y-1">
          <Label className="text-xs">
            {t("workspace.agent.basicForm.formBuilder.baseField.placeholder")}
          </Label>
          <Input
            value={field.placeholder}
            onChange={(e) =>
              onChange({ ...field, placeholder: e.target.value } as T)
            }
            disabled={disabled}
          />
        </div>
      ) : null}
      <div className="space-y-1">
        <Label className="text-xs flex items-center gap-1">
          {t("workspace.agent.basicForm.formBuilder.baseField.analyticsLabel")}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                  aria-label={t(
                    "workspace.agent.basicForm.formBuilder.baseField.analyticsLabelHelpAria"
                  )}
                >
                  <CircleHelp className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-sm text-left">
                <p>
                  {t(
                    "workspace.agent.basicForm.formBuilder.baseField.analyticsLabelHelp"
                  )}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Label>
        <Input
          value={field.label}
          onChange={(e) =>
            onChange({
              ...field,
              label: e.target.value,
            } as T)
          }
          disabled={disabled}
          placeholder={t(
            "workspace.agent.basicForm.formBuilder.baseField.analyticsLabelPlaceholder"
          )}
        />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">
          {t("workspace.agent.basicForm.formBuilder.baseField.required")}
        </Label>
        <div className="flex items-center h-10">
          <Switch
            checked={field.required}
            onCheckedChange={(checked) =>
              onChange({ ...field, required: checked } as T)
            }
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  )
}

export default BaseFieldEditor
